import { useEffect, useRef, useState } from "react"
import { validateDestinationUrl } from "../../lib/urls"
import MailtoSuggestion from "../MailtoSuggestion"
import DestinationInput from "../DestinationInput"
import { previewRepoint, commitRepoint, undoRepoint } from "../../lib/api"
import { cleanMessage } from "./GroupsDialog"

// Change where many codes point, in one go
// (docs/plans/2026-09-20-bulk-repoint.plan.md).
//
// Three steps, and the middle one is the point: every repoint is previewed on
// the server before anything is written. The preview names the count and every
// code that will be skipped, with why — the SPA can't work that out itself,
// because whether a code is still editable is a server rule, and a second copy
// of the 50-scan rule here is how a bulk path quietly becomes a bypass.
//
//   compose  → one URL for the selection, or a CSV (token,destination_url)
//   review   → the server's dry run; confirm writes exactly this roster
//   done     → what changed, with Undo for the whole batch
//
// The only upgrade prompt is the count of codes skipped for being past their
// free 50 scans. Row caps and the daily budget are abuse guards; their errors
// come from the server and name a contact path, never the upgrade page.
const REASONS = {
  scan_paywall:        "past the free 50 scans",
  disabled:            "paused",
  integrity_locked:    "under review",
  destination_unsafe:  "destination flagged as unsafe",
  not_found:           "not one of your codes",
  invalid_destination: "not a valid destination",
  failed:              "couldn't be saved — try again",
  edited_since:        "edited again since — left as they are",
}

const noun = (n) => `${n.toLocaleString()} ${n === 1 ? "code" : "codes"}`

export default function RepointDialog({ selection, count, filter, onClose, onDone, onUpgrade }) {
  const dialogRef = useRef(null)
  const [mode, setMode] = useState("url") // "url" | "csv"
  const [url, setUrl] = useState("")
  const [file, setFile] = useState(null)
  const [step, setStep] = useState("compose") // "compose" | "review" | "done"
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [undone, setUndone] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [suggestion, setSuggestion] = useState(null)

  // Closing after a write must still refresh the list behind the dialog.
  const wroteRef = useRef(false)
  function close() {
    if (wroteRef.current) onDone()
    onClose()
  }
  const closeRef = useRef(close)
  closeRef.current = close

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    function handleCancel(e) { e.preventDefault(); closeRef.current() }
    function handleClick(e) { if (e.target === dialog) closeRef.current() }
    dialog?.addEventListener("cancel", handleCancel)
    dialog?.addEventListener("click", handleClick)
    return () => {
      dialog?.removeEventListener("cancel", handleCancel)
      dialog?.removeEventListener("click", handleClick)
    }
  }, [])

  function request() {
    if (mode === "csv") return { file }
    const destinationUrl = url.trim()
    return selection.all
      ? { destinationUrl, match: { q: filter.q, group: filter.group } }
      : { destinationUrl, tokens: [ ...selection.tokens ] }
  }

  async function run(action) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(cleanMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const review = () => {
    setSuggestion(null)
    if (mode === "url") {
      // The server refuses these too, but only here can the error name the
      // picked kind, and offer the mailto: fix in one click.
      const { error: message, suggestion: fix } = validateDestinationUrl(url)
      if (message) { setError(message); setSuggestion(fix); return }
    }
    return reviewOnServer()
  }

  const reviewOnServer = () => run(async () => {
    setPreview(await previewRepoint(request()))
    setStep("review")
  })

  const confirm = () => run(async () => {
    const written = await commitRepoint(request())
    wroteRef.current = written.updated > 0
    setResult(written)
    setStep("done")
  })

  const undo = () => run(async () => {
    setUndone(await undoRepoint(result.batch_id))
    wroteRef.current = true
  })

  const canReview = !busy && (mode === "csv" ? !!file : url.trim().length > 0)

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="repoint-dialog-title"
      className="p-0 m-auto bg-transparent backdrop:bg-coal/55 backdrop:backdrop-blur-sm"
    >
      <div className="w-[min(92vw,480px)] max-h-[85dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud rounded-xl shadow-lg overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-[color:var(--color-border)] dark:border-storm">
          <div>
            <h2 id="repoint-dialog-title" className="font-display font-bold text-lg">Change destination</h2>
            <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-0.5">
              Printed codes keep working — they just go somewhere new.
            </p>
          </div>
          <button type="button" onClick={close} aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud p-1 bg-transparent border-0 cursor-pointer">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {step === "compose" && (
            <Compose mode={mode} setMode={(m) => { setMode(m); setError(null) }}
              count={count} url={url} setUrl={setUrl} file={file} setFile={setFile}
              onSubmit={() => canReview && review()} />
          )}
          {step === "review" && preview && <Review preview={preview} isCsv={mode === "csv"} onUpgrade={onUpgrade} />}
          {step === "done" && result && <Done result={result} undone={undone} onUpgrade={onUpgrade} />}
          {error && (
            <p role="alert" className="mt-3 text-xs text-fail">
              {error}
              <MailtoSuggestion
                suggestion={suggestion}
                onApply={(next) => { setUrl(next); setSuggestion(null); setError(null) }}
              />
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[color:var(--color-border)] dark:border-storm">
          {step === "compose" && (
            <>
              <GhostButton onClick={close}>Cancel</GhostButton>
              <PrimaryButton onClick={review} disabled={!canReview}>{busy ? "Checking…" : "Review"}</PrimaryButton>
            </>
          )}
          {step === "review" && (
            <>
              <GhostButton onClick={() => { setStep("compose"); setError(null) }} disabled={busy}>Back</GhostButton>
              <PrimaryButton onClick={confirm} disabled={busy || preview.updated === 0}>
                {busy ? "Changing…" : preview.updated === 0 ? "Nothing to change" : `Change ${noun(preview.updated)}`}
              </PrimaryButton>
            </>
          )}
          {step === "done" && (
            <>
              {result.batch_id && !undone && (
                <GhostButton onClick={undo} disabled={busy}>{busy ? "Undoing…" : "Undo"}</GhostButton>
              )}
              <PrimaryButton onClick={close}>Done</PrimaryButton>
            </>
          )}
        </div>
      </div>
    </dialog>
  )
}

function Compose({ mode, setMode, count, url, setUrl, file, setFile, onSubmit }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
      <div role="tablist" aria-label="How to change destinations" className="inline-flex rounded border border-[color:var(--color-border)] dark:border-storm p-0.5 mb-4">
        {[ [ "url", `One URL for ${noun(count)}` ], [ "csv", "Upload a CSV" ] ].map(([ value, label ]) => (
          <button key={value} type="button" role="tab" aria-selected={mode === value} onClick={() => setMode(value)}
            className={`px-3 py-1.5 text-sm rounded-[10px] transition ${mode === value
              ? "bg-coal text-cloud dark:bg-cloud dark:text-coal font-semibold"
              : "text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud"}`}>
            {label}
          </button>
        ))}
      </div>

      {mode === "url" ? (
        <div>
          <span id="repoint-destination-label" className="block text-sm font-semibold mb-1.5">New destination</span>
          <DestinationInput
            value={url}
            onChange={setUrl}
            autoFocus
            pickerClassName="border border-[color:var(--color-border)] dark:border-storm rounded pl-3 pr-2 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-2 dark:focus:ring-offset-midnight"
            inputClassName="w-full font-mono border border-[color:var(--color-border)] dark:border-storm rounded px-3 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-2 dark:focus:ring-offset-midnight focus:border-voltage"
            inputProps={{ "aria-labelledby": "repoint-destination-label" }}
          />
          <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
            Every selected code will point here. You'll see exactly what changes before anything does.
          </span>
        </div>
      ) : (
        <div>
          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">CSV file</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-plasma file:text-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-plasma-dark file:cursor-pointer"
            />
          </label>
          {file && <p className="mt-1.5 font-mono text-xs truncate">{file.name}</p>}
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-2">
            One row per code, each with its own destination. The file names its own codes — your selection doesn't apply. Header row required:
          </p>
          <pre className="mt-1.5 font-mono text-xs bg-[color:var(--color-cloud)] dark:bg-coal rounded px-3 py-2 overflow-x-auto">{"token,destination_url\naBc123,https://example.com/store-12"}</pre>
        </div>
      )}
    </form>
  )
}

// Skipped rows, grouped by reason, each reason expandable to its tokens.
function SkippedList({ skipped }) {
  const byReason = skipped.reduce((acc, s) => {
    (acc[s.reason] ||= []).push(s.token)
    return acc
  }, {})
  const reasons = Object.keys(byReason)
  if (reasons.length === 0) return null

  return (
    <div className="mt-3">
      <div className="text-sm font-semibold mb-1">{noun(skipped.length)} will be skipped</div>
      <ul className="space-y-1">
        {reasons.map((reason) => (
          <li key={reason}>
            <details className="text-sm">
              <summary className="cursor-pointer text-[color:var(--color-muted)] dark:text-muted-light">
                <span className="font-mono">{byReason[reason].length.toLocaleString()}</span> {REASONS[reason] || reason}
              </summary>
              <p className="mt-1 ml-4 font-mono text-xs break-all">{byReason[reason].join(", ")}</p>
            </details>
          </li>
        ))}
      </ul>
    </div>
  )
}

// The one earned upgrade prompt: codes left alone because they've proven
// themselves past the free 50 scans.
function PaywallPrompt({ skipped, onUpgrade }) {
  const locked = skipped.filter((s) => s.reason === "scan_paywall").length
  if (locked === 0) return null
  return (
    <div className="mt-4 rounded-lg border border-plasma/40 bg-plasma-pale dark:bg-plasma/15 px-3 py-2.5 flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm">
        {locked === 1 ? "1 code is" : `${locked.toLocaleString()} codes are`} past the free 50 scans.
        Upgrade to change {locked === 1 ? "it" : "them all"}.
      </span>
      <button type="button" onClick={() => (onUpgrade ? onUpgrade("locked") : window.location.assign("/pricing"))}
        className="bg-plasma text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-plasma-dark transition">
        Upgrade to Pro
      </button>
    </div>
  )
}

function Review({ preview, isCsv, onUpgrade }) {
  return (
    <div>
      <p className="text-base">
        <span className="font-mono font-semibold">{preview.updated.toLocaleString()}</span>
        {` ${preview.updated === 1 ? "code" : "codes"} will change`}
        {!isCsv && <> of <span className="font-mono">{preview.matched.toLocaleString()}</span> selected</>}.
      </p>
      {preview.unchanged > 0 && (
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
          {noun(preview.unchanged)} already {preview.unchanged === 1 ? "points" : "point"} there.
        </p>
      )}
      <SkippedList skipped={preview.skipped} />
      <PaywallPrompt skipped={preview.skipped} onUpgrade={onUpgrade} />
      {preview.updated > 0 && (
        <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-4">
          You can undo this as one change afterwards.
        </p>
      )}
    </div>
  )
}

function Done({ result, undone, onUpgrade }) {
  if (undone) {
    return (
      <div role="status">
        <p className="text-base">
          Undone. <span className="font-mono font-semibold">{undone.restored.toLocaleString()}</span>
          {` ${undone.restored === 1 ? "code points" : "codes point"} where ${undone.restored === 1 ? "it" : "they"} did before.`}
        </p>
        <SkippedList skipped={undone.skipped} />
        <PaywallPrompt skipped={undone.skipped} onUpgrade={onUpgrade} />
      </div>
    )
  }
  return (
    <div role="status">
      <p className="text-base">
        Changed <span className="font-mono font-semibold">{result.updated.toLocaleString()}</span>
        {` ${result.updated === 1 ? "code" : "codes"}.`}
      </p>
      {result.unchanged > 0 && (
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
          {noun(result.unchanged)} already pointed there.
        </p>
      )}
      <SkippedList skipped={result.skipped} />
      <PaywallPrompt skipped={result.skipped} onUpgrade={onUpgrade} />
    </div>
  )
}

function PrimaryButton({ children, ...props }) {
  return (
    <button type="button" {...props}
      className="bg-voltage text-coal rounded-lg px-4 py-2 text-sm font-semibold hover:bg-voltage-dark transition disabled:opacity-50 disabled:cursor-not-allowed">
      {children}
    </button>
  )
}

function GhostButton({ children, ...props }) {
  return (
    <button type="button" {...props}
      className="rounded-lg px-4 py-2 text-sm font-medium border border-[color:var(--color-border)] dark:border-storm hover:border-voltage transition disabled:opacity-50">
      {children}
    </button>
  )
}
